import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileOutput, FolderArchive, Loader2, RefreshCw } from 'lucide-react';
import { apiJson, apiBlob } from '@/lib/api';
import { buildReportImagesZipFilename } from '@shared/report-images-zip-filename';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface ReadinessIssue { code: string; message: string; count?: number }
interface ReportReadiness {
  campaign?: {
    campaignName: string;
    cycleName: string;
  };
  template: { id: number; name: string; version: string; sourceFileType: string } | null;
  eligibleImageCount: number;
  retailerReadyImageCount: number;
  excludedImageCount: number;
  pendingDecisionCount: number;
  storeCount: number;
  totalCampaignStoreCount: number;
  missingStoreCount: number;
  formatCount: number;
  estimatedEvidenceSlides: number;
  estimatedTotalSlides: number;
  blockingErrors: ReadinessIssue[];
  warnings: ReadinessIssue[];
  pages?: Array<{
    slideTitle: string;
    pageNumber: number;
    pageCount: number;
    formatId: number | null;
    formatName: string | null;
    items: Array<{
      evidenceAssetId: number;
      storeId: number;
      storeName: string;
      storeNumber: string | null;
      formatName: string | null;
      imageUrl?: string;
      cropRecipe?: {
        crop?: {
          x: number;
          y: number;
          width: number;
          height: number;
        } | null;
      } | null;
    }>;
  }>;
}
interface CampaignReport {
  id: number;
  templateName: string;
  templateVersion: string;
  status: 'GENERATING' | 'GENERATED' | 'APPROVED' | 'SENT' | 'FAILED' | 'ARCHIVED';
  outputFileType: string | null;
  failureMessage: string | null;
  generatedAt: string | null;
  createdAt: string;
  evidenceImageCount: number;
}

const statusVariant = (status: CampaignReport['status']) => status === 'FAILED' ? 'destructive' : status === 'GENERATED' ? 'secondary' : status === 'ARCHIVED' ? 'outline' : 'default';

export function ReportsTab({ campaignId }: { campaignId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [previewItem, setPreviewItem] = useState<{
    imageUrl: string;
    storeName: string;
    cropRecipe?: {
      crop?: {
        x: number;
        y: number;
        width: number;
        height: number;
      } | null;
    } | null;
  } | null>(null);
  const [downloadingImages, setDownloadingImages] = useState<Set<number>>(new Set());
  const readinessKey = ['/api/campaigns', campaignId, 'reports', 'readiness'];
  const reportsKey = ['/api/campaigns', campaignId, 'reports'];
  const readinessQuery = useQuery<ReportReadiness>({
    queryKey: readinessKey,
    queryFn: async () => (await apiJson<{ data: ReportReadiness }>(`/api/campaigns/${campaignId}/reports/readiness`)).data,
    retry: false,
  });
  const reportsQuery = useQuery<CampaignReport[]>({
    queryKey: reportsKey,
    queryFn: async () => (await apiJson<{ data: { reports: CampaignReport[] } }>(`/api/campaigns/${campaignId}/reports`)).data.reports,
    refetchInterval: (query) => query.state.data?.some((report) => report.status === 'GENERATING') ? 3000 : false,
    retry: false,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(`/api/campaigns/${campaignId}/reports/generate`, 'POST');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportsKey });
      queryClient.invalidateQueries({ queryKey: readinessKey });
      toast({ title: 'Report generated', description: 'The PowerPoint report is available in report history.' });
    },
    onError: (error: any) => toast({ title: 'Generation failed', description: error?.data?.blockingErrors?.map((issue: ReadinessIssue) => issue.message).join(' ') || error.message, variant: 'destructive' }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ reportId, status }: { reportId: number; status: 'APPROVED' | 'SENT' | 'ARCHIVED' }) => apiRequest(`/api/campaigns/${campaignId}/reports/${reportId}/status`, 'PUT', { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportsKey }),
    onError: (error: Error) => toast({ title: 'Status update failed', description: error.message, variant: 'destructive' }),
  });

  const downloadReport = async (reportId: number) => {
    try {
      const response = await apiJson<{ data: { url: string; filename?: string } }>(`/api/campaigns/${campaignId}/reports/${reportId}/download`);
      const link = document.createElement('a');
      link.href = response.data.url;
      if (response.data.filename) link.download = response.data.filename;
      link.click();
    } catch (error: any) {
      toast({ title: 'Download failed', description: error.message, variant: 'destructive' });
    }
  };

  const downloadImages = async (report: CampaignReport) => {
    const campaignCtx = readinessQuery.data?.campaign;
    if (!campaignCtx?.campaignName) {
      toast({
        title: 'Image download failed',
        description: 'Campaign name is not available yet. Refresh report readiness and try again.',
        variant: 'destructive',
      });
      return;
    }

    setDownloadingImages((prev) => new Set(prev).add(report.id));
    try {
      const { blob } = await apiBlob(`/api/campaigns/${campaignId}/reports/${report.id}/images/download`);
      const filename = buildReportImagesZipFilename(campaignCtx.campaignName, campaignCtx.cycleName);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({ title: 'Image download failed', description: error.message, variant: 'destructive' });
    } finally {
      setDownloadingImages((prev) => { const next = new Set(prev); next.delete(report.id); return next; });
    }
  };

  const readiness = readinessQuery.data;
  const previewPages = readiness?.pages ?? [];
  const blockingErrors = readiness?.blockingErrors ?? [];
  const warnings = readiness?.warnings ?? [];
  const blocked = !readiness || blockingErrors.length > 0;
  const isWorkInProgress = Boolean(readiness && readiness.pendingDecisionCount > 0);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div><CardTitle>Report readiness</CardTitle><p className="mt-1 text-sm text-muted-foreground">Evidence and template checks for an in-app compliance report.</p></div>
          <Button variant="outline" size="sm" onClick={() => readinessQuery.refetch()} disabled={readinessQuery.isFetching}><RefreshCw className={`mr-2 h-4 w-4 ${readinessQuery.isFetching ? 'animate-spin' : ''}`} />Refresh</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {readinessQuery.isLoading && <div className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Checking report readiness…</div>}
          {readinessQuery.isError && <Alert variant="destructive"><AlertTitle>Readiness check failed</AlertTitle><AlertDescription>{(readinessQuery.error as Error).message}</AlertDescription></Alert>}
          {readiness && <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {[['Template', readiness.template ? `${readiness.template.name} ${readiness.template.version}` : 'Not configured'], ['Included images', readiness.eligibleImageCount], ['Image decisions', `${readiness.retailerReadyImageCount - readiness.pendingDecisionCount} / ${readiness.retailerReadyImageCount}`], ['Stores represented', `${readiness.storeCount} / ${readiness.totalCampaignStoreCount}`], ['Estimated slides', readiness.estimatedTotalSlides]].map(([label, value]) => <div key={String(label)} className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 font-semibold">{value}</div></div>)}
            </div>
            {blockingErrors.map((issue) => <Alert key={issue.code} variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertTitle>Generation blocked</AlertTitle><AlertDescription>{issue.message}</AlertDescription></Alert>)}
            {warnings.map((issue) => <Alert key={issue.code} variant="warning"><AlertTriangle className="h-4 w-4" /><AlertTitle>{issue.code === 'REPORT_READY_DECISIONS_PENDING' ? 'Work in progress' : 'Review recommended'}</AlertTitle><AlertDescription>{issue.message}</AlertDescription></Alert>)}
            {!blocked && <Alert variant="success"><CheckCircle2 className="h-4 w-4" /><AlertTitle>{isWorkInProgress ? 'Ready for a work-in-progress report' : 'Ready to generate'}</AlertTitle><AlertDescription>{isWorkInProgress ? `This version will include the ${readiness.eligibleImageCount} image${readiness.eligibleImageCount === 1 ? '' : 's'} currently in Report Ready. You can process more images and generate a new version later.` : `Image review is complete. The report will use ${readiness.eligibleImageCount} included image${readiness.eligibleImageCount === 1 ? '' : 's'} and omit ${readiness.excludedImageCount} excluded image${readiness.excludedImageCount === 1 ? '' : 's'}.`}</AlertDescription></Alert>}
            <Button onClick={() => generateMutation.mutate()} disabled={blocked || generateMutation.isPending}>
              {generateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileOutput className="mr-2 h-4 w-4" />}
              {isWorkInProgress
                ? (reportsQuery.data?.length ? 'Generate new work-in-progress version' : 'Generate work-in-progress report')
                : (reportsQuery.data?.length ? 'Generate new version' : 'Generate report')}
            </Button>
          </>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Report preview</CardTitle>
          <p className="text-sm text-muted-foreground">
            Planned slide structure based only on images explicitly included in the Report Ready pool. The generated file will include the
            overview slide followed by these evidence slides.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {readinessQuery.isLoading && <div className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing report preview…</div>}
          {readiness && previewPages.length === 0 && <p className="text-sm text-muted-foreground">No evidence slides are available to preview yet.</p>}
          {readiness && previewPages.length > 0 && (
            <div className="space-y-3">
              <div className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">Slide 1: Campaign overview</div>
                    <div className="text-sm text-muted-foreground">
                      Campaign summary and retailer reporting context.
                    </div>
                  </div>
                  <Badge variant="outline">Overview</Badge>
                </div>
              </div>
              {previewPages.map((page, index) => (
                <div key={`${page.slideTitle}-${page.pageNumber}-${index}`} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">Slide {index + 2}: {page.slideTitle}</div>
                      <div className="text-sm text-muted-foreground">
                        {page.items.length} image{page.items.length === 1 ? '' : 's'} from {new Set(page.items.map((item) => item.storeId)).size} store{new Set(page.items.map((item) => item.storeId)).size === 1 ? '' : 's'}
                      </div>
                    </div>
                    {page.pageCount > 1 && <Badge variant="secondary">Page {page.pageNumber} of {page.pageCount}</Badge>}
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {page.items.map((item) => (
                      <button
                        type="button"
                        key={`${item.evidenceAssetId}-${item.storeId}`}
                        className={`flex w-full gap-3 rounded-md bg-muted p-2 text-left text-sm ${item.imageUrl ? 'cursor-pointer transition-colors hover:bg-muted/80' : ''}`}
                        disabled={!item.imageUrl}
                        onClick={() => item.imageUrl && setPreviewItem({
                          imageUrl: item.imageUrl,
                          storeName: item.storeName,
                          cropRecipe: item.cropRecipe,
                        })}
                      >
                        {item.imageUrl && (
                          <div className="relative h-10 aspect-[4/7] shrink-0 overflow-hidden rounded border bg-black">
                            {item.cropRecipe?.crop ? (
                              <div
                                className="absolute"
                                style={{
                                  width: `${100 / item.cropRecipe.crop.width}%`,
                                  height: `${100 / item.cropRecipe.crop.height}%`,
                                  left: `${-(item.cropRecipe.crop.x / item.cropRecipe.crop.width) * 100}%`,
                                  top: `${-(item.cropRecipe.crop.y / item.cropRecipe.crop.height) * 100}%`,
                                }}
                              >
                                <img
                                  src={item.imageUrl}
                                  alt={item.storeName}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <img
                                src={item.imageUrl}
                                alt={item.storeName}
                                className="h-full w-full object-cover"
                              />
                            )}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{item.storeName}</div>
                          {item.storeNumber && <div className="text-xs text-muted-foreground">Store {item.storeNumber}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Report history</CardTitle></CardHeader>
        <CardContent>
          {reportsQuery.isError && <Alert variant="destructive"><AlertTitle>Report history unavailable</AlertTitle><AlertDescription>{(reportsQuery.error as Error).message}</AlertDescription></Alert>}
          {!reportsQuery.isLoading && reportsQuery.data?.length === 0 && <p className="text-sm text-muted-foreground">No reports have been generated for this campaign.</p>}
          {!!reportsQuery.data?.length && <Table>
            <TableHeader><TableRow><TableHead>Created</TableHead><TableHead>Template</TableHead><TableHead>Status</TableHead><TableHead>Details</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{reportsQuery.data.map((report) => <TableRow key={report.id}>
              <TableCell>{new Date(report.generatedAt || report.createdAt).toLocaleString()}</TableCell>
              <TableCell>{report.templateName} <Badge variant="outline" className="ml-1">{report.templateVersion}</Badge></TableCell>
              <TableCell><Badge variant={statusVariant(report.status)}>{report.status}</Badge></TableCell>
              <TableCell className="max-w-xs text-sm text-muted-foreground">
                {report.failureMessage || (report.outputFileType
                  ? `${report.outputFileType.toUpperCase()} · ${report.evidenceImageCount} image${report.evidenceImageCount === 1 ? '' : 's'}`
                  : '—')}
              </TableCell>
              <TableCell className="text-right space-x-2">
                {!['GENERATING', 'FAILED'].includes(report.status) && <Button variant="outline" size="sm" onClick={() => downloadReport(report.id)}><Download className="mr-1 h-4 w-4" />Download Report</Button>}
                {!['GENERATING', 'FAILED'].includes(report.status) && <Button variant="outline" size="sm" onClick={() => downloadImages(report)} disabled={downloadingImages.has(report.id)}>{downloadingImages.has(report.id) ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FolderArchive className="mr-1 h-4 w-4" />}Download Images</Button>}
                {report.status === 'GENERATED' && <Button size="sm" onClick={() => statusMutation.mutate({ reportId: report.id, status: 'APPROVED' })}>Approve</Button>}
                {report.status === 'APPROVED' && <Button size="sm" onClick={() => statusMutation.mutate({ reportId: report.id, status: 'SENT' })}>Mark sent</Button>}
                {!['ARCHIVED', 'GENERATING'].includes(report.status) && <Button variant="ghost" size="sm" onClick={() => statusMutation.mutate({ reportId: report.id, status: 'ARCHIVED' })}>Archive</Button>}
              </TableCell>
            </TableRow>)}</TableBody>
          </Table>}
        </CardContent>
      </Card>

      <Dialog open={!!previewItem} onOpenChange={(open) => !open && setPreviewItem(null)}>
        <DialogContent className="max-w-md border-none bg-black/90 p-0 text-white overflow-hidden">
          <DialogTitle className="sr-only">Evidence preview</DialogTitle>
          <div className="relative h-[80vh] aspect-[4/7] mx-auto overflow-hidden bg-muted">
            {previewItem && (
              previewItem.cropRecipe?.crop ? (
                <div
                  className="absolute"
                  style={{
                    width: `${100 / previewItem.cropRecipe.crop.width}%`,
                    height: `${100 / previewItem.cropRecipe.crop.height}%`,
                    left: `${-(previewItem.cropRecipe.crop.x / previewItem.cropRecipe.crop.width) * 100}%`,
                    top: `${-(previewItem.cropRecipe.crop.y / previewItem.cropRecipe.crop.height) * 100}%`,
                  }}
                >
                  <img
                    src={previewItem.imageUrl}
                    alt={previewItem.storeName}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <img
                  src={previewItem.imageUrl}
                  alt={previewItem.storeName}
                  className="h-full w-full object-cover"
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
